// src/components/organisms/SelectionToolbar/SelectionToolbar.jsx
import React from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Button,
  Typography,
  Chip,
  Tooltip,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  useTheme,
  Alert,
  Collapse,
  CircularProgress
} from '@mui/material';
import {
  Clear as ClearIcon,
  Favorite as FavoriteIcon,
  LocalOffer as TagIcon,
  LocalOfferOutlined as TagOutlinedIcon,
  Collections as CollectionIcon,
  CollectionsBookmarkOutlined as CollectionOutlinedIcon,
  SentimentSatisfied as SentimentIcon,
  Visibility as ReadIcon,
  Delete as DeleteIcon,
  RemoveCircle as RemoveTagIcon,
  RemoveFromQueue as RemoveCollectionIcon,
  CheckBox as CheckBoxIcon,
  CheckBoxOutlineBlank,
  Undo as UndoIcon,
  FileDownload as ExportIcon,
  TableRows as TableRowsIcon,
  ViewHeadline as ViewHeadlineIcon,
  Email as EmailActionIcon,
  Edit as EditIcon,
  FactCheck as NativeReviewIcon,
  AutoAwesome as AutoAwesomeIcon
} from '@mui/icons-material';
import QuickSelectionMenu from '../../molecules/QuickSelectionMenu/QuickSelectionMenu';
import {
  getSelectionToolbarTokens,
  operationalElevation,
  operationalRadii,
  operationalToolbars,
  operationalTypography
} from '../../../design-system/tokens';

/**
 * SelectionToolbar - Componente organismo para toolbar de seleção
 * 
 * Este componente exibe as ações disponíveis quando o modo de seleção
 * está ativo. Segue o princípio do Atomic Design como um "organismo".
 * 
 * MELHORIAS IMPLEMENTADAS:
 * - Contador de itens selecionados (sem redundância)
 * - Ícones nos botões
 * - Tooltips explicativos
 * - Melhor organização visual
 * - Feedback visual aprimorado
 * - Compatibilidade com design system
 * - Espaçamento adequado entre elementos
 */
const SelectionToolbar = ({
  onSelectAll,
  onEditFields,
  onNativeReview,
  onFavorite,
  onAddTag,
  onRemoveTags,
  onAddToCollection,
  onRemoveFromCollections,
  onSetSentiment,
  onSetRead,
  onDelete,
  onSendNewsletter,
  onBulkRescrapeSmart,
  onBulkAnalyzeKpia,
  onClearSelection,
  onExportExcel,
  allSelected = false,
  selectedCount = 0,
  showRemoveFromTag = false,
  onRemoveFromTag,
  showRemoveFromCollection = false,
  onRemoveFromCollection,
  selectionMode = false,
  onToggleSelectionMode,
  // Novas props para seleção avançada
  quickSelectionOptions = [],
  onQuickSelect,
  onSelectInverse,
  onUndoLastAction,
  canUndo = false,
  // Props para seleção estilo Gmail
  totalCount = 0,
  currentPageCount = 0,
  onSelectAllPages,
  selectingAllPages = false,
  bulkActionLoading = false,
  bulkActionProgress = null, // Novo: progresso da operação em lote
  className = '',
  ...props
}) => {
  const theme = useTheme();
  const [showSelectAllPrompt, setShowSelectAllPrompt] = React.useState(false);
  const [exportMenuAnchor, setExportMenuAnchor] = React.useState(null);
  const isDark = theme.palette.mode === 'dark';
  const toolbarTokens = getSelectionToolbarTokens(isDark);
  const toolbarLayout = operationalToolbars.selection;
  const iconButtonSx = {
    width: toolbarLayout.iconButton.width,
    height: toolbarLayout.iconButton.height,
    borderRadius: `${operationalRadii.control}px`,
    color: toolbarTokens.text,
    opacity: 0.9,
    '&:hover': {
      backgroundColor: toolbarTokens.hover,
      opacity: 1
    },
    '&.Mui-disabled': {
      color: toolbarTokens.text,
      opacity: 0.35
    }
  };
  const dividerSx = {
    height: 28,
    borderColor: toolbarTokens.divider
  };

  // Determinar o ícone do botão de seleção
  const getSelectionButtonIcon = () => {
    // Se tem seleção e todos da página atual estão selecionados OU todos os artigos estão selecionados
    if (allSelected || (selectedCount > 0 && selectedCount >= currentPageCount)) {
      return <CheckBoxIcon />; // Mostra checked
    }
    return <CheckBoxOutlineBlank />; // Mostra vazio
  };

  // Handler para fechar o toolbar e sair do modo seleção
  const handleClose = () => {
    onClearSelection(); // Limpar seleção
    if (onToggleSelectionMode) {
      onToggleSelectionMode(); // Desativar modo seleção
    }
  };

  // Handler para selecionar/limpar todos (estilo Gmail)
  const handleToggleSelectAll = () => {
    if (allSelected) {
      // Se todos já estão selecionados, limpar
      onClearSelection();
      setShowSelectAllPrompt(false);
    } else {
      // Selecionar apenas a página atual
      onSelectAll();

      // Se tem mais itens além da página atual, mostrar prompt
      if (totalCount > currentPageCount && totalCount > selectedCount) {
        setShowSelectAllPrompt(true);
      }
    }
  };

  // Handler para selecionar todos os itens (incluindo os não carregados)
  const handleSelectAllPages = () => {
    if (onSelectAllPages) {
      onSelectAllPages();
      setShowSelectAllPrompt(false);
    }
  };

  // Esconder o prompt quando limpar seleção ou quando todos já estão selecionados
  React.useEffect(() => {
    if (selectedCount === 0 || allSelected) {
      setShowSelectAllPrompt(false);
    }
  }, [selectedCount, allSelected]);

  // Só mostrar se estiver em modo seleção
  if (!selectionMode) {
    return null;
  }

  return (
    <>
      <Box
        sx={{
          position: 'fixed',
          bottom: 80,
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 1200,
          width: { xs: 'calc(100vw - 32px)', sm: 'max-content' },
          maxWidth: 'calc(100vw - 32px)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'stretch'
        }}
      >
        {/* Prompt para selecionar todos os itens como extensao da toolbar */}
        <Collapse in={showSelectAllPrompt} sx={{ width: '100%' }}>
          <Alert
            severity="info"
            action={
              <Button
                variant="contained"
                size="small"
                onClick={handleSelectAllPages}
                disabled={selectingAllPages}
                startIcon={selectingAllPages ? <CircularProgress size={16} sx={{ color: theme.palette.primary.main }} /> : null}
                sx={{
                  fontWeight: 'bold',
                  backgroundColor: toolbarTokens.selected,
                  color: toolbarTokens.selectedText,
                  borderRadius: `${operationalRadii.control}px`,
                  px: 1.75,
                  whiteSpace: 'nowrap',
                  border: `1px solid ${toolbarTokens.border}`,
                  boxShadow: 'none',
                  '&:hover': {
                    backgroundColor: toolbarTokens.selected,
                    boxShadow: 'none'
                  },
                  '&.Mui-disabled': {
                    backgroundColor: toolbarTokens.selected,
                    color: toolbarTokens.selectedText,
                    opacity: 0.7
                  }
                }}
              >
                {selectingAllPages ? 'Selecionando...' : 'Selecionar todos'}
              </Button>
            }
            onClose={() => setShowSelectAllPrompt(false)}
            sx={{
              backgroundColor: toolbarTokens.surface,
              color: toolbarTokens.text,
              border: '1px solid',
              borderColor: toolbarTokens.border,
              borderBottom: 'none',
              borderRadius: `${operationalRadii.surface}px ${operationalRadii.surface}px 0 0`,
              boxShadow: operationalElevation.raised,
              minHeight: toolbarLayout.prompt.minHeight,
              py: 0.35,
              px: 1.25,
              alignItems: 'center',
              position: 'relative',
              '& .MuiAlert-message': {
                display: 'flex',
                alignItems: 'center',
                paddingTop: '2px',
                color: toolbarTokens.text,
                whiteSpace: { xs: 'normal', sm: 'nowrap' },
                '& strong': {
                  color: toolbarTokens.text
                }
              },
              '& .MuiAlert-icon': {
                color: toolbarTokens.text,
                paddingTop: '2px'
              },
              '& .MuiAlert-action': {
                paddingTop: 0,
                paddingBottom: 0,
                alignItems: 'center',
                color: toolbarTokens.text
              },
              '& .MuiIconButton-root': {
                color: toolbarTokens.text
              }
            }}
          >
            {selectedCount} {selectedCount === 1 ? 'item selecionado' : 'itens selecionados'}.{' '}
            <strong>Selecionar todos os {totalCount} artigos?</strong>
          </Alert>
        </Collapse>

      {/* SelectionToolbar */}
      <Box
        className={`selection-toolbar ${className}`}
        sx={{
          display: 'flex',
          gap: 0.6,
          alignItems: 'center',
          backgroundColor: toolbarTokens.surface,
          backgroundImage: 'none',
          color: toolbarTokens.text,
          border: '1px solid',
          borderColor: toolbarTokens.border,
          borderRadius: showSelectAllPrompt
            ? `0 0 ${operationalRadii.surface}px ${operationalRadii.surface}px`
            : `${operationalRadii.surface}px`,
          boxShadow: operationalElevation.modal,
          py: 0.75,
          px: 1,
          maxWidth: 'calc(100vw - 32px)',
          overflowX: 'auto',
          animation: 'slideUp 0.18s ease-out',
          '@keyframes slideUp': {
            from: {
              transform: 'translateY(100%)',
              opacity: 0
            },
            to: {
              transform: 'translateY(0)',
              opacity: 1
            }
          },
          // Global icon size for all icons in this toolbar
          '& .MuiSvgIcon-root': {
            fontSize: operationalTypography.selectionToolbar.iconSize
          }
        }}
        {...props}
      >
        {/* Indicador de Progresso - Aparece quando há progresso sendo mostrado */}
        {bulkActionProgress && (
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            px: 1,
            py: 0.5,
            backgroundColor: toolbarTokens.hover,
            borderRadius: `${operationalRadii.control}px`
          }}>
            <CircularProgress
              size={18}
              thickness={5}
              variant={bulkActionProgress.percentage !== undefined ? "determinate" : "indeterminate"}
              value={bulkActionProgress.percentage}
              sx={{ color: toolbarTokens.text }}
            />
            <Typography
              variant="body2"
              sx={{
                color: toolbarTokens.text,
                fontWeight: 600,
                fontSize: operationalTypography.selectionToolbar.progressFontSize,
                whiteSpace: 'nowrap'
              }}
            >
              {bulkActionProgress.message || `${bulkActionProgress.current}/${bulkActionProgress.total}`}
            </Typography>
          </Box>
        )}

        {/* Contador de seleção compacto */}
        {!bulkActionProgress && (
          <Chip
            label={(selectingAllPages || bulkActionLoading)
              ? <CircularProgress size={14} sx={{ color: theme.palette.primary.main }} />
              : selectedCount}
            size="small"
            sx={{
              fontWeight: 700,
              fontSize: operationalTypography.selectionToolbar.counterFontSize,
              minWidth: toolbarLayout.counter.minWidth,
              height: toolbarLayout.counter.height,
              backgroundColor: toolbarTokens.selected,
              color: toolbarTokens.selectedText,
              border: `1px solid ${toolbarTokens.border}`,
              borderRadius: `${operationalRadii.control}px`,
              '& .MuiChip-label': {
                color: toolbarTokens.selectedText
              }
            }}
          />
        )}

        <Divider orientation="vertical" flexItem sx={dividerSx} />

        {/* Botão de Selecionar/Limpar Todos */}
        <Tooltip title={allSelected ? "Limpar todos" : "Selecionar todos"}>
          <IconButton
            size="medium"
            onClick={handleToggleSelectAll}
            sx={iconButtonSx}
          >
            {getSelectionButtonIcon()}
          </IconButton>
        </Tooltip>

        {/* Menu de Seleção Rápida */}
        {onQuickSelect && quickSelectionOptions.length > 0 && (
          <QuickSelectionMenu
            options={quickSelectionOptions}
            onSelect={onQuickSelect}
            sx={{
              height: 34,
              borderRadius: `${operationalRadii.control}px`,
              borderColor: 'transparent',
              color: toolbarTokens.text,
              minWidth: 0,
              px: 1,
              '&:hover': {
                backgroundColor: toolbarTokens.hover,
                borderColor: 'transparent',
                color: toolbarTokens.text,
                boxShadow: 'none'
              }
            }}
          />
        )}

        <Divider orientation="vertical" flexItem sx={dividerSx} />

        {/* Ações Principais - Apenas Ícones */}
        {onEditFields && (
          <Tooltip title={bulkActionLoading ? "Processando..." : "Editar campos"}>
            <span>
              <IconButton
                size="medium"
                onClick={onEditFields}
                disabled={bulkActionLoading}
                sx={iconButtonSx}
              >
                <EditIcon />
              </IconButton>
            </span>
          </Tooltip>
        )}

        {onNativeReview && (
          <Tooltip title={bulkActionLoading ? "Processando..." : "Revisao nativa"}>
            <span>
              <IconButton
                size="medium"
                onClick={onNativeReview}
                disabled={bulkActionLoading}
                sx={iconButtonSx}
              >
                <NativeReviewIcon />
              </IconButton>
            </span>
          </Tooltip>
        )}

        <Tooltip title={bulkActionLoading ? "Processando..." : "Favoritos"}>
          <span>
            <IconButton
              size="medium"
              onClick={onFavorite}
              disabled={bulkActionLoading}
              sx={iconButtonSx}
            >
              <FavoriteIcon />
            </IconButton>
          </span>
        </Tooltip>

        <Tooltip title={bulkActionLoading ? "Processando..." : "Adicionar Tags"}>
          <span>
            <IconButton
              size="medium"
              onClick={onAddTag}
              disabled={bulkActionLoading}
              sx={iconButtonSx}
            >
              <TagIcon />
            </IconButton>
          </span>
        </Tooltip>

        {onRemoveTags && (
          <Tooltip title={bulkActionLoading ? "Processando..." : "Remover Tags"}>
            <span>
              <IconButton
                size="medium"
                onClick={onRemoveTags}
                disabled={bulkActionLoading}
                sx={iconButtonSx}
              >
                <TagOutlinedIcon />
              </IconButton>
            </span>
          </Tooltip>
        )}

        <Tooltip title={bulkActionLoading ? "Processando..." : "Adicionar à Coleção"}>
          <span>
            <IconButton
              size="medium"
              onClick={onAddToCollection}
              disabled={bulkActionLoading}
              sx={iconButtonSx}
            >
              <CollectionIcon />
            </IconButton>
          </span>
        </Tooltip>

        {onRemoveFromCollections && (
          <Tooltip title={bulkActionLoading ? "Processando..." : "Remover de Coleções"}>
            <span>
              <IconButton
                size="medium"
                onClick={onRemoveFromCollections}
                disabled={bulkActionLoading}
                sx={iconButtonSx}
              >
                <CollectionOutlinedIcon />
              </IconButton>
            </span>
          </Tooltip>
        )}

        <Tooltip title={bulkActionLoading ? "Processando..." : "Sentimento"}>
          <span>
            <IconButton
              size="medium"
              onClick={onSetSentiment}
              disabled={bulkActionLoading}
              sx={iconButtonSx}
            >
              <SentimentIcon />
            </IconButton>
          </span>
        </Tooltip>

        <Tooltip title={bulkActionLoading ? "Processando..." : "Marcar como Lido"}>
          <span>
            <IconButton
              size="medium"
              onClick={onSetRead}
              disabled={bulkActionLoading}
              sx={iconButtonSx}
            >
              <ReadIcon />
            </IconButton>
          </span>
        </Tooltip>

        <Tooltip title={bulkActionLoading ? "Processando..." : "Excluir"}>
          <span>
            <IconButton
              size="medium"
              onClick={onDelete}
              disabled={bulkActionLoading}
              sx={iconButtonSx}
            >
              <DeleteIcon />
            </IconButton>
          </span>
        </Tooltip>

        {onSendNewsletter && (
          <Tooltip title={bulkActionLoading ? "Processando..." : "Enviar como Newsletter"}>
            <span>
              <IconButton
                size="medium"
                onClick={onSendNewsletter}
                disabled={bulkActionLoading}
                sx={iconButtonSx}
              >
                <EmailActionIcon />
              </IconButton>
            </span>
          </Tooltip>
        )}

        {onBulkRescrapeSmart && (
          <Tooltip title={bulkActionLoading ? "Processando..." : "Recarregar Dados"}>
            <span>
              <IconButton
                size="medium"
                onClick={onBulkRescrapeSmart}
                disabled={bulkActionLoading}
                sx={iconButtonSx}
              >
                <AutoAwesomeIcon />
              </IconButton>
            </span>
          </Tooltip>
        )}

        {onBulkAnalyzeKpia && (
          <Tooltip title={bulkActionLoading ? "Processando..." : "Análise Qualitativa (Lote)"}>
            <span>
              <IconButton
                size="medium"
                onClick={onBulkAnalyzeKpia}
                disabled={bulkActionLoading}
                sx={iconButtonSx}
              >
                <AutoAwesomeIcon />
              </IconButton>
            </span>
          </Tooltip>
        )}

        {/* Botão de Exportar — abre menu com as duas opções */}
        {onExportExcel && (
          <>
            <Tooltip title={bulkActionLoading ? "Processando..." : "Exportar para Excel"}>
              <span>
                <IconButton
                  size="medium"
                  onClick={(e) => setExportMenuAnchor(e.currentTarget)}
                  disabled={bulkActionLoading}
                  sx={iconButtonSx}
                >
                  <ExportIcon />
                </IconButton>
              </span>
            </Tooltip>

            {/* Menu dropdown de modo de exportação */}
            <Menu
              anchorEl={exportMenuAnchor}
              open={Boolean(exportMenuAnchor)}
              onClose={() => setExportMenuAnchor(null)}
              anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
              transformOrigin={{ vertical: 'bottom', horizontal: 'center' }}
              PaperProps={{
                sx: {
                  borderRadius: `${operationalRadii.surface}px`,
                  minWidth: 280,
                  boxShadow: operationalElevation.raised
                }
              }}
            >
              <Box sx={{ px: 2, pt: 1.5, pb: 0.5 }}>
                <Typography variant="caption" color="text.secondary" fontWeight={600} textTransform="uppercase" letterSpacing={0.5}>
                  Formato de Exportação
                </Typography>
              </Box>

              {/* Opção 1: Uma linha por tag (atual) */}
              <MenuItem
                onClick={() => {
                  setExportMenuAnchor(null);
                  onExportExcel('per_line');
                }}
                sx={{ py: 1.5, px: 2 }}
              >
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <ViewHeadlineIcon color="primary" />
                </ListItemIcon>
                <ListItemText
                  primary={<Typography fontWeight={600} fontSize="0.9rem">Uma linha por tag</Typography>}
                  secondary="Relatório com duplicidade de artigos — o artigo se repete para cada tag"
                  secondaryTypographyProps={{ fontSize: '0.78rem' }}
                />
              </MenuItem>

              {/* Opção 2: Tags por vírgula (novo) */}
              <MenuItem
                onClick={() => {
                  setExportMenuAnchor(null);
                  onExportExcel('comma');
                }}
                sx={{ py: 1.5, px: 2 }}
              >
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <TableRowsIcon color="primary" />
                </ListItemIcon>
                <ListItemText
                  primary={<Typography fontWeight={600} fontSize="0.9rem">Tags por vírgula</Typography>}
                  secondary="Relatório sem duplicidade de artigos — uma linha por artigo com tags na mesma célula"
                  secondaryTypographyProps={{ fontSize: '0.78rem' }}
                />
              </MenuItem>
            </Menu>
          </>
        )}

        <Divider orientation="vertical" flexItem sx={dividerSx} />

        {/* Botão de Fechar (sai do modo seleção) */}
        <Tooltip title="Fechar e sair do modo seleção">
          <IconButton
            size="medium"
            onClick={handleClose}
            sx={iconButtonSx}
          >
            <ClearIcon />
          </IconButton>
        </Tooltip>
      </Box>
      </Box>
    </>
  );
};

SelectionToolbar.propTypes = {
  onSelectAll: PropTypes.func.isRequired,
  onEditFields: PropTypes.func,
  onNativeReview: PropTypes.func,
  onFavorite: PropTypes.func.isRequired,
  onAddTag: PropTypes.func.isRequired,
  onRemoveTags: PropTypes.func,
  onAddToCollection: PropTypes.func.isRequired,
  onRemoveFromCollections: PropTypes.func,
  onSetSentiment: PropTypes.func.isRequired,
  onSetRead: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onClearSelection: PropTypes.func.isRequired,
  onExportExcel: PropTypes.func,
  onSendNewsletter: PropTypes.func,
  onBulkRescrapeSmart: PropTypes.func,
  onBulkAnalyzeKpia: PropTypes.func,
  allSelected: PropTypes.bool,
  selectedCount: PropTypes.number,
  showRemoveFromTag: PropTypes.bool,
  onRemoveFromTag: PropTypes.func,
  showRemoveFromCollection: PropTypes.bool,
  onRemoveFromCollection: PropTypes.func,
  selectionMode: PropTypes.bool,
  onToggleSelectionMode: PropTypes.func,

  // Novas props para seleção avançada
  quickSelectionOptions: PropTypes.array,
  onQuickSelect: PropTypes.func,
  onSelectInverse: PropTypes.func,
  onUndoLastAction: PropTypes.func,
  canUndo: PropTypes.bool,

  // Props para seleção estilo Gmail
  totalCount: PropTypes.number,
  currentPageCount: PropTypes.number,
  onSelectAllPages: PropTypes.func,
  selectingAllPages: PropTypes.bool,
  bulkActionLoading: PropTypes.bool,
  bulkActionProgress: PropTypes.shape({
    current: PropTypes.number,
    total: PropTypes.number,
    percentage: PropTypes.number,
    message: PropTypes.string
  }),

  className: PropTypes.string
};

export default SelectionToolbar;
